import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  Header,
  HStack,
  Icon,
  RippleButton,
  Spinner,
  Text,
  VStack,
} from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { ROUTESWEB, AVATAR_ITEMS, ROUTES } from "@repo/utils";
import {
  UploadFailurePopup,
  UploadPendingPopup,
  UploadSuccessPopup,
  ConfirmPopup,
} from "../../components/UploadResultPopup";
import { ExamSubmission } from "../../components/ExamSubmission";
import { RateExperienceDrawer } from "../../components/RateExperienceDrawer";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useIsTablet } from "../../hooks/useIsTablet";
import { usePdfViewerSubmission } from "../../hooks/usePdfViewerSubmission";
import { useUploadAnswerSheet } from "../../hooks/useUploadAnswerSheet";
import { useTestSeriesLabel } from "../../hooks/useTestSeriesLabel";
import layoutStyles from "../../components/TestListLayout/TestListLayout.module.scss";
import type { ExamRouteState } from "../../types";
import { serverNow } from "../../../../shared/serverClock";
import { useIsClockSynced } from "../../context/ServerClockContext";
import styles from "./PdfViewer.module.scss";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface PdfViewerBreadcrumbStep {
  label: string;
  href?: string;
  back?: boolean;
}

interface PdfViewerLocationState {
  file?: File;
  fileName?: string;
  pdfUrl?: string;
  examId?: string;
  sourceRoute?: string;
  examState?: ExamRouteState;
  viewOnly?: boolean;
  backLabel?: string;
  breadcrumbTrail?: PdfViewerBreadcrumbStep[];
  currentBreadcrumb?: string;
}

const MIN_SCALE = 0.6;
const MAX_SCALE = 2.5;
const SCALE_STEP = 0.2;
const BASE_PAGE_WIDTH = 480;
const PAGE_HORIZONTAL_PADDING = 32;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const PdfViewerPage = () => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isMobileOrTablet = isMobile || isTablet;
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { t } = useTranslation("tests");
  const testSeriesLabel = useTestSeriesLabel();
  const [isPendingPopupDismissed, setIsPendingPopupDismissed] = useState(false);
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const successTransitionTimerRef = useRef<number | null>(null);

  const locationState = (location.state ?? {}) as PdfViewerLocationState;
  const {
    file,
    fileName,
    pdfUrl,
    viewOnly = false,
    backLabel,
    breadcrumbTrail,
    currentBreadcrumb,
  } = locationState;
  const examId = locationState.examId ?? "";
  const examState = locationState.examState ?? null;
  const pdfTitle = fileName ?? file?.name ?? t("upload_answer_sheet_flow.pdf");

  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [hasPdfError, setHasPdfError] = useState(false);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const pageWidth = isMobileOrTablet
    ? Math.max(240, window.innerWidth - PAGE_HORIZONTAL_PADDING)
    : BASE_PAGE_WIDTH;

  const pdfSource = useMemo(() => file ?? pdfUrl, [file, pdfUrl]);

  const isClockSynced = useIsClockSynced();
  const hasExamTimeLeft = useMemo(() => {
    if (!examState || !isClockSynced) return false;
    const examEndMs = examState.examEndTime
      ? new Date(examState.examEndTime).getTime()
      : examState.examStartedAt && examState.durationMinutes
        ? new Date(examState.examStartedAt).getTime() +
          examState.durationMinutes * 60_000
        : 0;
    return examEndMs > 0 && serverNow() < examEndMs;
  }, [examState, isClockSynced]);

  const { isUploading, error, isSuccess, uploadFile, reset } =
    useUploadAnswerSheet(examId);

  const {
    isSubmitting,
    handleConfirmSubmit,
    handleResumeExam,
    isRateExperienceOpen,
    handleFeedbackSubmit,
    handleRateExperienceClose,
    isFeedbackSubmitting,
  } = usePdfViewerSubmission(examId, examState);

  useEffect(() => {
    if (viewOnly) return;
    if (!examId) {
      navigate(`/${ROUTESWEB.APP.TESTS}`, { replace: true });
    }
  }, [examId, navigate, viewOnly]);

  useEffect(() => {
    return () => {
      if (pdfUrl && pdfUrl.startsWith("blob:")) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    if (viewOnly) return;
    if (!isUploading) {
      setIsPendingPopupDismissed(false);
    }
  }, [isUploading, viewOnly]);

  useEffect(() => {
    if (viewOnly) return;
    if (!isSuccess) {
      setIsSuccessPopupVisible(false);
      if (successTransitionTimerRef.current != null) {
        window.clearTimeout(successTransitionTimerRef.current);
        successTransitionTimerRef.current = null;
      }
      return;
    }

    setIsSuccessPopupVisible(true);
    if (successTransitionTimerRef.current != null) {
      window.clearTimeout(successTransitionTimerRef.current);
    }
    successTransitionTimerRef.current = window.setTimeout(() => {
      setIsSuccessPopupVisible(false);
      setIsConfirmPopupOpen(true);
    }, 2000);

    return () => {
      if (successTransitionTimerRef.current != null) {
        window.clearTimeout(successTransitionTimerRef.current);
        successTransitionTimerRef.current = null;
      }
    };
  }, [isSuccess, viewOnly]);

  useEffect(() => {
    if (totalPages <= 0) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          const pageNumber = Number(
            (visible.target as HTMLElement).dataset.pageNumber,
          );
          if (!Number.isNaN(pageNumber)) setCurrentPage(pageNumber);
        }
      },
      { root: container, threshold: [0.25, 0.5, 0.75] },
    );

    Object.values(pageRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [totalPages]);

  const handleUpload = useCallback(() => {
    if (file) {
      setIsPendingPopupDismissed(false);
      setIsSuccessPopupVisible(false);
      setIsConfirmPopupOpen(false);
      uploadFile(file);
    }
  }, [file, uploadFile]);

  const handleBackToUpload = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleCloseSuccessPopup = useCallback(() => {
    reset();
    setIsSuccessPopupVisible(false);
    setIsConfirmPopupOpen(false);
  }, [reset]);

  const handleRetryUpload = useCallback(() => {
    reset();
    setIsConfirmPopupOpen(false);
    navigate(-1);
  }, [navigate, reset]);

  const handleCloseConfirmPopup = useCallback(() => {
    setIsConfirmPopupOpen(false);
  }, []);

  const handleSubmitExam = useCallback(async () => {
    setIsConfirmPopupOpen(false);
    const isSubmitted = await handleConfirmSubmit();
    if (isSubmitted) {
      reset();
    }
  }, [handleConfirmSubmit, reset]);

  const handleZoomIn = () =>
    setScale((prev) => clamp(prev + SCALE_STEP, MIN_SCALE, MAX_SCALE));
  const handleZoomOut = () =>
    setScale((prev) => clamp(prev - SCALE_STEP, MIN_SCALE, MAX_SCALE));

  const headerTitleStyles = useMemo(
    () =>
      isMobileOrTablet
        ? {
            fontSize: "1rem",
            fontWeight: 600,
            color: theme.colors.neutral[10],
            whiteSpace: "nowrap" as const,
            overflow: "hidden" as const,
            textOverflow: "ellipsis" as const,
            display: "block" as const,
            maxWidth: "100%",
          }
        : undefined,
    [isMobileOrTablet, theme.colors.neutral],
  );

  const headerTitleContainerStyles = useMemo(
    () =>
      isMobileOrTablet
        ? {
            flex: 1,
            minWidth: 0,
            overflow: "hidden" as const,
            marginLeft: "var(--spacing-md)",
            marginRight: "var(--spacing-md)",
          }
        : undefined,
    [isMobileOrTablet],
  );

  return (
    <VStack className={styles.container}>
      <div className={styles.headerWrap}>
        <Header
          title={isMobileOrTablet ? pdfTitle : undefined}
          titleAlign={isMobileOrTablet ? "left" : "center"}
          titleStyles={headerTitleStyles}
          titleContainerStyles={headerTitleContainerStyles}
          left={
            <Box
              onClick={() => navigate(-1)}
              className={layoutStyles.backButton}
            >
              <Icon
                name="arrowBackIcon"
                size={24}
                color={theme.colors.text[10]}
              />
            </Box>
          }
          rightIcon={isMobileOrTablet ? undefined : "userProfileIcon"}
          rightIconItems={isMobileOrTablet ? undefined : AVATAR_ITEMS}
          right={isMobileOrTablet ? <div /> : undefined}
          showRight={!isMobileOrTablet}
          breadcrumb={
            isMobileOrTablet ? undefined : viewOnly &&
              breadcrumbTrail?.length ? (
              <Breadcrumb>
                {breadcrumbTrail.map((step, idx) => {
                  const onClick = step.back
                    ? () => navigate(-1)
                    : step.href
                      ? () => navigate(step.href!)
                      : undefined;
                  return (
                    <Fragment key={`trail-${idx}`}>
                      {idx > 0 && <BreadcrumbSeparator />}
                      <BreadcrumbItem>
                        <BreadcrumbLink onClick={onClick}>
                          {step.label}
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                    </Fragment>
                  );
                })}
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink isActive>
                    {currentBreadcrumb ?? t("upload_answer_sheet_flow.pdf")}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </Breadcrumb>
            ) : (
              <Breadcrumb>
                <BreadcrumbItem key="test">
                  <BreadcrumbLink
                    onClick={() => navigate(`/${ROUTESWEB.APP.TESTS}`)}
                  >
                    {t("test")}
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator key="sep1" />
                <BreadcrumbItem key="source">
                  <BreadcrumbLink
                    onClick={() => navigate(`/${ROUTES.APP.TESTS.ILTS}`)}
                  >
                    {testSeriesLabel}
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {!viewOnly && (
                  <>
                    <BreadcrumbSeparator key="sep2" />
                    <BreadcrumbItem key="answer-sheet">
                      <BreadcrumbLink onClick={handleBackToUpload}>
                        {t("upload_answer_sheet_flow.upload_answer_sheet")}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                  </>
                )}
                {viewOnly && backLabel && (
                  <>
                    <BreadcrumbSeparator key="sep-back" />
                    <BreadcrumbItem key="back">
                      <BreadcrumbLink onClick={() => navigate(-1)}>
                        {backLabel}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                  </>
                )}
                <BreadcrumbSeparator key="sep3" />
                <BreadcrumbItem key="current">
                  <BreadcrumbLink isActive>
                    {currentBreadcrumb ?? t("upload_answer_sheet_flow.pdf")}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </Breadcrumb>
            )
          }
        />
      </div>

      <Box className={styles.pdfSurface} ref={scrollContainerRef}>
        {isPdfLoading && !hasPdfError && (
          <Box className={styles.loadingOverlay}>
            <Spinner size="lg" />
          </Box>
        )}

        {hasPdfError && (
          <Box className={styles.errorOverlay}>
            <Text variant="body" size="large" weight="regular">
              {t("upload_answer_sheet_flow.pdf_preview_fallback")}
            </Text>
          </Box>
        )}

        {pdfSource && (
          <Document
            file={pdfSource}
            loading={null}
            noData={null}
            error={null}
            onLoadSuccess={({ numPages }: { numPages: number }) => {
              setTotalPages(numPages);
              setCurrentPage(1);
              setIsPdfLoading(false);
              setHasPdfError(false);
            }}
            onLoadError={() => {
              setIsPdfLoading(false);
              setHasPdfError(true);
            }}
            onSourceError={() => {
              setIsPdfLoading(false);
              setHasPdfError(true);
            }}
            className={styles.pdfDocument}
          >
            <Box className={styles.pageStack}>
              {Array.from({ length: totalPages }, (_, index) => (
                <div
                  key={`page-${index + 1}`}
                  className={styles.pageWrap}
                  data-page-number={index + 1}
                  ref={(node) => {
                    pageRefs.current[index + 1] = node;
                  }}
                >
                  <Page
                    pageNumber={index + 1}
                    width={pageWidth}
                    scale={scale}
                    renderTextLayer
                    renderAnnotationLayer
                    loading={
                      <div className={styles.pageStatus}>
                        Loading page {index + 1}…
                      </div>
                    }
                    error={
                      <div className={styles.pageStatus}>
                        Error rendering page {index + 1}
                      </div>
                    }
                    onRenderError={(e) => {
                      console.error(`[PDF] page ${index + 1} render error`, e);
                    }}
                    onLoadError={(e) => {
                      console.error(`[PDF] page ${index + 1} load error`, e);
                    }}
                  />
                </div>
              ))}
              {!isMobileOrTablet && (
                <div className={styles.bottomSpacer} aria-hidden />
              )}
            </Box>
          </Document>
        )}

        {viewOnly && totalPages > 0 && (
          <HStack className={styles.zoomPill} align="center" gap="md">
            <Text
              variant="body"
              size="large"
              weight="emphasized"
              color={theme.colors.neutral[95]}
            >
              {currentPage}/{totalPages}
            </Text>
            <HStack align="center">
              <Box
                as="button"
                className={styles.zoomButton}
                onClick={handleZoomIn}
                aria-label={t("upload_answer_sheet_flow.pdf")}
                disabled={scale >= MAX_SCALE}
              >
                <Icon
                  name="zoomIn"
                  size={24}
                  color={theme.colors.neutral[95]}
                />
              </Box>
              <Box
                as="button"
                className={styles.zoomButton}
                onClick={handleZoomOut}
                aria-label={t("upload_answer_sheet_flow.pdf")}
                disabled={scale <= MIN_SCALE}
              >
                <Icon
                  name="zoomOut"
                  size={24}
                  color={theme.colors.neutral[95]}
                />
              </Box>
            </HStack>
          </HStack>
        )}
      </Box>

      {!viewOnly && (
        <HStack className={styles.bottomBar}>
          <RippleButton
            variant="primary"
            size="md"
            shape="round"
            onPress={handleUpload}
            disabled={isUploading}
          >
            {t("upload_answer_sheet_flow.upload_pdf_button")}
          </RippleButton>
        </HStack>
      )}

      {!viewOnly && (
        <>
          <UploadSuccessPopup
            isOpen={isSuccess && isSuccessPopupVisible}
            onClose={handleCloseSuccessPopup}
          />

          <ConfirmPopup
            isOpen={isConfirmPopupOpen}
            onClose={handleCloseConfirmPopup}
            onResumeExam={handleResumeExam}
            onSubmitExam={handleSubmitExam}
            isSubmitting={isSubmitting}
            hideResume={!hasExamTimeLeft}
          />

          <ExamSubmission isOpen={isSubmitting} onClose={() => {}} />

          <UploadPendingPopup
            isOpen={isUploading && !isPendingPopupDismissed}
            onClose={() => setIsPendingPopupDismissed(true)}
          />

          <UploadFailurePopup
            isOpen={Boolean(error)}
            onClose={reset}
            onAction={handleRetryUpload}
          />

          <RateExperienceDrawer
            isOpen={isRateExperienceOpen}
            onClose={handleRateExperienceClose}
            onSubmit={handleFeedbackSubmit}
            isSubmitting={isFeedbackSubmitting}
          />
        </>
      )}
    </VStack>
  );
};
